import { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Autocomplete,
  FormControlLabel,
  Switch,
  Divider,
  Collapse,
} from '@mui/material';
import {
  PersonAdd,
  MoreVert,
  Edit,
  Delete,
  Block,
  CheckCircle,
} from '@mui/icons-material';
import {
  DataGridPremium,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usersService, User, CreateUserInput, UpdateUserInput } from '../../services/users.service';
import { WorkspaceSelect } from '../../components/WorkspaceSelect';
import { partnersService, Partner } from '../../services/partners.service';
import { useAuthStore } from '../../stores/auth.store';
import ReportDefaultsForm from '../../components/ReportDefaultsForm';
import AppearanceField from '../../components/AppearanceField';
import type { TrackingDefaults } from '../../utils/user-settings';
import { BirthdayField } from '../../components/BirthdayField';
import { displayBirthday } from '../../utils/birthday';


const roleColors: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  admin: 'error',
  media_buyer: 'warning',
  viewer: 'default',
  partner: 'info',
};

const statusColors: Record<string, 'success' | 'warning' | 'error'> = {
  active: 'success',
  pending: 'warning',
  inactive: 'error',
};

/** "no preference" sentinel for the config selects — MUI Select can't hold undefined. */

export default function UsersPage() {
  const queryClient = useQueryClient();
  const currentUserId = useAuthStore((s) => s.user?.id);
  const setUserSettings = useAuthStore((s) => s.setUserSettings);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuUser, setMenuUser] = useState<User | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isPartnerUser, setIsPartnerUser] = useState(false);
  const [formData, setFormData] = useState<CreateUserInput>({
    email: '',
    first_name: '',
    last_name: '',
    role: 'viewer',
  });
  const [editData, setEditData] = useState<UpdateUserInput>({});
  const [jsonOpen, setJsonOpen] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonErr, setJsonErr] = useState<string | null>(null);
  const [jsonCopied, setJsonCopied] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['users', page, pageSize],
    queryFn: () => usersService.query({ offset: page * pageSize, limit: pageSize }),
  });

  const { data: partners } = useQuery({
    queryKey: ['partners'],
    queryFn: partnersService.list,
    staleTime: 60_000,
  });

  const createMutation = useMutation({
    mutationFn: usersService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setCreateDialogOpen(false);
      setFormData({ email: '', first_name: '', last_name: '', role: 'viewer' });
      setIsPartnerUser(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserInput }) =>
      usersService.update(id, data),
    onSuccess: (updated, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // Editing yourself: update the store so the reports pick this up without a re-login.
      if (id === currentUserId) setUserSettings(updated.settings || {});
      setEditDialogOpen(false);
      setMenuUser(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: usersService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setDeleteDialogOpen(false);
      setMenuUser(null);
    },
  });

  const columns: GridColDef[] = [
    {
      field: 'avatar',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Avatar
          src={params.row.avatar_url}
          alt={`${params.row.first_name} ${params.row.last_name}`}
          sx={{ width: 32, height: 32 }}
        >
          {params.row.first_name?.[0] || params.row.email[0].toUpperCase()}
        </Avatar>
      ),
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 180,
      valueGetter: (_, row) => {
        if (row.first_name || row.last_name) {
          return `${row.first_name || ''} ${row.last_name || ''}`.trim();
        }
        return '-';
      },
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 220,
    },
    {
      field: 'role',
      headerName: 'Role',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={roleColors[params.value] || 'default'}
          variant="outlined"
        />
      ),
    },
    {
      field: 'partner_name',
      headerName: 'Partner',
      width: 150,
      renderCell: (params: GridRenderCellParams) =>
        params.value ? (
          <Chip label={params.value} size="small" color="secondary" variant="outlined" />
        ) : null,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value}
          size="small"
          color={statusColors[params.value] || 'default'}
          icon={params.value === 'active' ? <CheckCircle /> : params.value === 'inactive' ? <Block /> : undefined}
        />
      ),
    },
    {
      field: 'last_login_at',
      headerName: 'Last Login',
      width: 160,
      valueFormatter: (value) =>
        value ? new Date(value).toLocaleString() : 'Never',
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      valueFormatter: (value) =>
        new Date(value).toLocaleDateString(),
    },
    {
      field: 'birthday',
      headerName: 'Birthday',
      width: 110,
      valueFormatter: (value) => (value ? displayBirthday(value) : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
            setMenuUser(params.row);
          }}
        >
          <MoreVert />
        </IconButton>
      ),
    },
  ];

  const handleOpenEdit = () => {
    if (menuUser) {
      setEditData({
        first_name: menuUser.first_name || '',
        last_name: menuUser.last_name || '',
        role: menuUser.role,
        status: menuUser.status,
        settings: menuUser.settings || {},
        workspaceIds: menuUser.workspace_ids ?? [],
        birthday: menuUser.birthday ?? '',
      });
      setJsonOpen(false);
      setJsonErr(null);
      setEditDialogOpen(true);
    }
    setAnchorEl(null);
  };

  const handleToggleStatus = () => {
    if (menuUser) {
      const newStatus = menuUser.status === 'active' ? 'inactive' : 'active';
      updateMutation.mutate({
        id: menuUser.id,
        data: { status: newStatus },
      });
    }
    setAnchorEl(null);
  };

  const selectedPartner = partners?.find((p) => p.id === formData.partner_id) || null;

  const trackingDefaults = editData.settings?.tracking || {};
  const setTrackingDefault = <K extends keyof TrackingDefaults>(
    key: K,
    value: TrackingDefaults[K] | undefined,
  ) => {
    setEditData((prev) => {
      const next = { ...(prev.settings?.tracking || {}) };
      // "No preference" is an absent key, not a placeholder value.
      if (value === undefined) delete next[key];
      else next[key] = value;
      return { ...prev, settings: { ...prev.settings, tracking: next } };
    });
  };

  const openJsonEditor = () => {
    setJsonText(JSON.stringify(editData.settings ?? {}, null, 2));
    setJsonErr(null);
    setJsonOpen(true);
  };
  const onJsonChange = (v: string) => {
    setJsonText(v);
    try {
      const parsed = JSON.parse(v);
      if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
        setJsonErr('Settings must be a JSON object'); return;
      }
      setEditData((prev) => ({ ...prev, settings: parsed }));
      setJsonErr(null);
    } catch {
      setJsonErr('Invalid JSON');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Users
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAdd />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load users. Please try again.
        </Alert>
      )}

      <DataGridPremium
        rows={data?.data || []}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total || 0}
        pagination
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(model: GridPaginationModel) => {
          setPage(model.page);
          setPageSize(model.pageSize);
        }}
        pageSizeOptions={[50, 100, 200, 400]}
        disableRowSelectionOnClick
        autoHeight
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 2,
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem onClick={handleOpenEdit}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          Edit
        </MenuItem>
        <MenuItem onClick={handleToggleStatus}>
          <ListItemIcon>
            {menuUser?.status === 'active' ? (
              <Block fontSize="small" />
            ) : (
              <CheckCircle fontSize="small" />
            )}
          </ListItemIcon>
          {menuUser?.status === 'active' ? 'Deactivate' : 'Activate'}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setDeleteDialogOpen(true);
            setAnchorEl(null);
          }}
          sx={{ color: 'error.main' }}
          disabled={false}
        >
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          Delete
        </MenuItem>
      </Menu>

      {/* Create User Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isPartnerUser}
                  onChange={(e) => {
                    setIsPartnerUser(e.target.checked);
                    if (!e.target.checked) {
                      setFormData({ ...formData, partner_id: undefined });
                    }
                  }}
                />
              }
              label="Partner user"
            />
            {isPartnerUser && (
              <Autocomplete
                options={partners || []}
                getOptionLabel={(o: Partner) => o.name}
                value={selectedPartner}
                onChange={(_, v) => setFormData({ ...formData, partner_id: v?.id || undefined })}
                renderInput={(params) => <TextField {...params} label="Partner" required />}
              />
            )}
            <TextField
              label="Email"
              type="email"
              required
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
            </Box>
            <TextField
              label="Password"
              type="password"
              required={isPartnerUser}
              fullWidth
              value={formData.password || ''}
              onChange={(e) => setFormData({ ...formData, password: e.target.value || undefined })}
              helperText={isPartnerUser ? 'Required for partner users.' : 'Optional. User can also sign in via Google.'}
            />
            {!isPartnerUser && (
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                >
                  <MenuItem value="viewer">Viewer</MenuItem>
                  <MenuItem value="media_buyer">Media Buyer</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
            )}
            {!isPartnerUser && formData.role !== 'admin' && (
              <WorkspaceSelect
                multiple
                label="Workspaces *"
                placeholder="Choose the workspaces this user can access"
                value={formData.workspaceIds ?? []}
                onChange={(v) => setFormData({ ...formData, workspaceIds: v as string[] })}
              />
            )}
          </Box>
          {createMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Failed to create user. Please try again.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => createMutation.mutate({
              ...formData,
              role: isPartnerUser ? 'viewer' : formData.role,
            })}
            disabled={!formData.email || (isPartnerUser && (!formData.partner_id || !formData.password))
              || (!isPartnerUser && formData.role !== 'admin' && !formData.workspaceIds?.length) || createMutation.isPending}
          >
            {createMutation.isPending ? 'Creating...' : 'Add User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Email"
              fullWidth
              value={menuUser?.email || ''}
              disabled
            />
            {menuUser?.partner_name && (
              <TextField label="Partner" fullWidth value={menuUser.partner_name} disabled />
            )}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="First Name"
                fullWidth
                value={editData.first_name || ''}
                onChange={(e) => setEditData({ ...editData, first_name: e.target.value })}
              />
              <TextField
                label="Last Name"
                fullWidth
                value={editData.last_name || ''}
                onChange={(e) => setEditData({ ...editData, last_name: e.target.value })}
              />
            </Box>
            <BirthdayField
              fullWidth
              value={editData.birthday || ''}
              onChange={(birthday) => setEditData({ ...editData, birthday })}
              helperText="Shown on the birthday celebration screen (leave empty to clear)"
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editData.role || 'viewer'}
                label="Role"
                onChange={(e) => setEditData({ ...editData, role: e.target.value as User['role'] })}
                disabled={false}
              >
                <MenuItem value="viewer">Viewer</MenuItem>
                <MenuItem value="media_buyer">Media Buyer</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {editData.role !== 'admin' && editData.role !== 'partner' && (
              <WorkspaceSelect
                multiple
                label="Workspaces"
                value={editData.workspaceIds ?? []}
                onChange={(v) => setEditData({ ...editData, workspaceIds: v as string[] })}
              />
            )}
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editData.status || 'active'}
                label="Status"
                onChange={(e) => setEditData({ ...editData, status: e.target.value as User['status'] })}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
              </Select>
            </FormControl>

            {/* one editor at a time: a form edit here can't be overwritten by a stale JSON re-parse */}
            {!jsonOpen && (
              <>
                <Divider sx={{ mt: 1 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={600}>
                    Report defaults
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    What Tracking, Clicks and Conversions open on for this user. Leave any
                    field on "Default" to use the shared setting.
                  </Typography>
                </Box>
                <AppearanceField
                  value={editData.settings?.theme_mode}
                  onChange={(v) => setEditData((prev) => ({
                    ...prev, settings: { ...prev.settings, theme_mode: v },
                  }))}
                />
                <ReportDefaultsForm value={trackingDefaults} onChange={setTrackingDefault} admin={editData.role === 'admin'} />
              </>
            )}

            <Divider sx={{ mt: 1 }} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="subtitle2" fontWeight={600}>Settings (JSON)</Typography>
                <Button
                  size="small"
                  onClick={() => {
                    // clear the error on Hide: its field unmounts, so a stuck jsonErr would disable Save invisibly
                    if (jsonOpen) { setJsonOpen(false); setJsonErr(null); } else openJsonEditor();
                  }}
                >
                  {jsonOpen ? 'Hide' : 'Edit as JSON'}
                </Button>
              </Box>
              <Collapse in={jsonOpen} unmountOnExit>
                <Typography variant="caption" color="text.secondary">
                  The whole settings object (presets, report defaults, theme). Copy/paste to clone between
                  users; saving merges each top-level key and drops unknown ones.
                </Typography>
                <TextField
                  fullWidth multiline minRows={6} value={jsonText}
                  onChange={(e) => onJsonChange(e.target.value)}
                  error={!!jsonErr} helperText={jsonErr || ' '}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '0.78rem' } }}
                  sx={{ mt: 1 }}
                />
                <Button
                  size="small"
                  onClick={() => {
                    navigator.clipboard?.writeText(jsonText)?.then(
                      () => { setJsonCopied(true); setTimeout(() => setJsonCopied(false), 1500); },
                      () => {},
                    );
                  }}
                >
                  {jsonCopied ? 'Copied!' : 'Copy'}
                </Button>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => menuUser && updateMutation.mutate({
              id: menuUser.id,
              data: { ...editData, birthday: editData.birthday || null },
            })}
            disabled={updateMutation.isPending || !!jsonErr
              || (editData.role !== 'admin' && editData.role !== 'partner' && !editData.workspaceIds?.length)}
          >
            {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete User?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{menuUser?.email}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => menuUser && deleteMutation.mutate(menuUser.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
